// inbox-91 / roadmap F1 — клиент для /api/v1/links (минимальная часть для
// onboarding-визарда: создание первой ссылки). Полный CRUD-клиент будет в G4
// (inbox-98 cabinet core) — не дублируем избыточно сейчас.

export interface Link {
  id: number;
  slug: string;
  short_url: string;
  // inbox-202 — короткий домен ссылки (justasecondgo.com / hopw.cc / vroo.cc / gonew.click).
  host: string;
  target_url: string;
  title: string | null;
  tags: string[];
  status: string;
  click_count: number;
  ttl_expires_at: string | null;
  click_limit: number | null;
  created_at: string;
  updated_at: string;
}

export interface LinkCreate {
  target_url: string;
  slug?: string;
  title?: string | null;
  tags?: string[];
  // inbox-202 — короткий домен (по умолчанию backend ставит 'justasecondgo.com').
  host?: string;
  // inbox-217 — optional привязка к кампании при создании. null = нет.
  campaign_id?: number | null;
}

/**
 * inbox-145 — поля для PATCH /api/v1/links/{id}.
 * Все поля optional; backend применяет только переданные (exclude_unset).
 */
export interface LinkUpdate {
  target_url?: string;
  title?: string | null;
  tags?: string[];
  status?: 'active' | 'paused';
  ttl_expires_at?: string | null;
  click_limit?: number | null;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch('/api/v1' + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = '';
    const cloned = res.clone();
    try {
      const j = await res.json();
      detail = j.detail || j.error || JSON.stringify(j);
    } catch {
      detail = await cloned.text();
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface LinksListParams {
  sort?: 'created_desc' | 'created_asc' | 'clicks_desc' | 'clicks_asc';
  limit?: number;
  offset?: number;
  status?: 'active' | 'paused' | 'expired';
  campaign_id?: number | null;
  search?: string;
}

// inbox-248 + inbox-250 — статистика per-link для деталки.
// Shape следует pydantic LinkStatsExtended (app/apps/api/schemas/link_stats.py,
// inbox-245). Period для breakdown'ов сервер обрабатывает в query-параметре.
export type StatsPeriod = '7d' | '30d' | '90d' | 'all';

export interface ReferrerRow {
  /** Host (e.g. 't.me'), 'direct' для пустого referer, или null если parser не извлёк. */
  referrer_domain: string | null;
  clicks: number;
  /** Доля от clicks_total (0.0–1.0, 4 знака). */
  share_pct: number;
}

export interface CountryRow {
  /** ISO-3166 alpha-2 (RU/US/...). null если MaxMind не определил. */
  country: string | null;
  clicks: number;
  unique_visitors: number;
  share_pct: number;
}

export interface DeviceRow {
  /** 'desktop' | 'mobile' | 'tablet' | 'bot' | 'unknown'. */
  device_type: string;
  clicks: number;
  share_pct: number;
}

export interface LinkStats {
  clicks_total: number;
  /** Клики за последние 7 дней (фиксированный период, не зависит от query.period). */
  clicks_7d: number;
  /** Клики за последние 30 дней (фиксированный период). */
  clicks_30d: number;
  /** DISTINCT visitor_id за всё время. */
  unique_visitors: number;
  /** Топ-10 source доменов с share_pct. Лимитировано query.period. */
  top_referrers: ReferrerRow[];
  /** Топ-10 стран. */
  top_countries: CountryRow[];
  /** Топ-10 device_type. */
  top_devices: DeviceRow[];
}

/**
 * Anon-link create / claim — inbox-132.
 *
 * POST /api/v1/links/anon — без auth, rate-limited (server side).
 * Принимает { target_url, title? }. Не путать с обычным `LinkCreate`
 * (slug / tags для anon не передаются — генерится сервером).
 *
 * POST /api/v1/links/claim — один slug на запрос (НЕ массив).
 * Требует залогиненного user'а. Привязывает anon-link к owner_id'у
 * вызывающего.
 */
export interface AnonLinkCreate {
  target_url: string;
  title?: string;
}

export interface AnonLink extends Link {
  expires_at: string | null;
}

export interface ClaimBody {
  slug: string;
}

export interface ClaimResult {
  link: Link;
}

export const LinksApi = {
  create(body: LinkCreate): Promise<Link> {
    return req<Link>('/links', { method: 'POST', body: JSON.stringify(body) });
  },
  /** inbox-145 — GET одной ссылки для EditLinkModal onMounted. */
  get(id: number): Promise<Link> {
    return req<Link>(`/links/${id}`);
  },
  /** inbox-145 — PATCH ссылки из EditLinkModal submit. */
  update(id: number, body: LinkUpdate): Promise<Link> {
    return req<Link>(`/links/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  },
  list(params: LinksListParams = {}): Promise<Link[]> {
    const q = new URLSearchParams();
    if (params.sort) q.set('sort', params.sort);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    if (params.status) q.set('status', params.status);
    if (params.campaign_id !== undefined && params.campaign_id !== null) q.set('campaign_id', String(params.campaign_id));
    if (params.search) q.set('search', params.search);
    const qs = q.toString();
    return req<Link[]>(`/links${qs ? '?' + qs : ''}`);
  },
  delete(id: number): Promise<void> {
    return req<void>(`/links/${id}`, { method: 'DELETE' });
  },
  /** inbox-248 — GET /api/v1/links/:id/stats?period=. */
  stats(id: number, period: StatsPeriod = '30d'): Promise<LinkStats> {
    return req<LinkStats>(`/links/${id}/stats?period=${period}`);
  },
  /**
   * Создать anon-ссылку (без auth). Backend ожидает {target_url, title?}.
   * Кидает ApiError(429, ...) при превышении rate-limit'а.
   */
  createAnon(body: AnonLinkCreate): Promise<AnonLink> {
    return req<AnonLink>('/links/anon', { method: 'POST', body: JSON.stringify(body) });
  },
  /**
   * Присвоить anon-ссылку залогиненному user'у. Один slug per call.
   */
  claim(body: ClaimBody): Promise<ClaimResult> {
    return req<ClaimResult>('/links/claim', { method: 'POST', body: JSON.stringify(body) });
  },
};
